import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { TEAM_PREVIEW_KEY, seatLabel, toTeamPreview } from "../../domain/team";
import { APPROVALS_PREVIEW_KEY, seatsToApprovalsPreview } from "../../domain/approvals";
import {
  fetchSeatsState,
  removeSeat,
  resetTabPolicies,
  setFunctionPolicy,
  setSeat,
  type SeatFunctionDTO,
  type SeatKind,
  type SeatMemberDTO,
  type SeatPersonDTO,
  type SeatPolicy,
  type SeatTabDTO,
  type SeatsStateDTO,
} from "../../data/seatsApi";
import { addTeamMember, fetchTeamState, type TeamStateDTO } from "../../data/teamApi";
import { persistAnswer, useCoClient } from "../../data/store";
import { CoButton } from "../../components/Button";
import { Callout } from "../../components/Callout";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  WarningIcon,
  XIcon,
} from "../../components/icons";
import { useSubStepNav } from "../useSubStepNav";

const INPUT_CLASS = "h-9 rounded-md border bg-background px-2 text-sm";

const POLICY_LABEL: Record<SeatPolicy, string> = {
  post: "Post Directly",
  review: "Send for Review",
  blocked: "Blocked",
};

/** Read under the selects, so "Blocked" never has to be guessed at. */
const POLICY_HINT: Record<SeatPolicy, string> = {
  post: "The Maker can complete and post without review.",
  review: "The Maker can perform the action, but a Checker must review and approve it.",
  blocked: "The Maker cannot perform this action.",
};

/** Review warm, Blocked muted — the approval screen's colour cues, carried over. */
function policyTone(policy: SeatPolicy): string {
  if (policy === "review") return "text-amber-700 dark:text-amber-400";
  if (policy === "blocked") return "text-muted-foreground";
  return "";
}

/** "Posted directly" / "Sent for review" / "Blocked for Makers", in policy order. */
const OUTCOME_HEADING: Record<SeatPolicy, string> = {
  post: "Posted directly",
  review: "Sent for review",
  blocked: "Blocked for Makers",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground",
        className
      )}
    >
      {initials(name)}
    </span>
  );
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "Priya", "Priya and Arjun", "Priya, Arjun and Deepa". */
function namesLine(people: SeatPersonDTO[]): string {
  const names = people.map((person) => firstName(person.name));
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// ── Assign: the two seat columns ────────────────────────────────────────────

/**
 * Somebody assignable to this tab: a roster member who is not already seated
 * on it, or a firm user who is not on the client at all. `onRoster` is what
 * decides whether picking them needs a team/add first — the roster stays the
 * identity home, so nobody can be seated who is not on it.
 */
interface SeatCandidate {
  user_id: number;
  name: string;
  email: string;
  onRoster: boolean;
}

function SeatPersonRow({
  person,
  busy,
  onRemove,
}: {
  person: SeatPersonDTO;
  busy: boolean;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border p-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar name={person.name} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{person.name}</span>
          <span className="truncate text-xs text-muted-foreground">
            {person.orphan ? "No longer on this client's team" : person.email}
          </span>
        </div>
      </div>
      <button
        type="button"
        aria-label={`Remove ${person.name}`}
        disabled={busy}
        onClick={onRemove}
        className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
      >
        <TrashIcon className="size-3.5" />
      </button>
    </li>
  );
}

/**
 * The attach flow, one seat at a time: a search over everybody who could take
 * this seat — the client's own roster first, then the wider firm directory.
 * There is no invite form here; everyone assignable already exists as a master
 * user.
 */
function AddSeatPicker({
  seat,
  candidates,
  busy,
  onPick,
}: {
  seat: SeatKind;
  candidates: SeatCandidate[];
  busy: boolean;
  onPick: (candidate: SeatCandidate) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  if (!open) {
    return (
      <CoButton variant="outline" className="w-full" onClick={() => setOpen(true)}>
        <PlusIcon className="size-3.5" /> Add a {seatLabel(seat)}
      </CoButton>
    );
  }

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? candidates.filter(
        (candidate) =>
          candidate.name.toLowerCase().includes(needle) || candidate.email.toLowerCase().includes(needle)
      )
    : candidates;
  const onClient = filtered.filter((candidate) => candidate.onRoster);
  const fromFirm = filtered.filter((candidate) => !candidate.onRoster);

  function closeAndReset() {
    setOpen(false);
    setQuery("");
  }

  const group = (label: string, people: SeatCandidate[]) =>
    people.length > 0 && (
      <>
        <li className="px-2 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </li>
        {people.map((candidate) => (
          <li key={candidate.user_id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                onPick(candidate);
                closeAndReset();
              }}
              className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-muted/40 disabled:opacity-50"
            >
              <Avatar name={candidate.name} className="size-7" />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{candidate.name}</span>
                <span className="truncate text-xs text-muted-foreground">{candidate.email}</span>
              </div>
            </button>
          </li>
        ))}
      </>
    );

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Add a {seatLabel(seat)}</span>
        <button
          type="button"
          aria-label={`Close add a ${seatLabel(seat)}`}
          onClick={closeAndReset}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/40"
        >
          <XIcon className="size-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-md border bg-background px-2.5">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search firm users"
          className="h-9 w-full bg-transparent text-sm outline-none"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground">No firm users left to seat here.</p>
      ) : (
        <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          {group("On this client", onClient)}
          {group("Elsewhere at the firm", fromFirm)}
        </ul>
      )}
    </div>
  );
}

function SeatColumn({
  seat,
  hint,
  people,
  candidates,
  busy,
  onPick,
  onRemove,
}: {
  seat: SeatKind;
  hint: string;
  people: SeatPersonDTO[];
  candidates: SeatCandidate[];
  busy: boolean;
  onPick: (candidate: SeatCandidate) => void;
  onRemove: (person: SeatPersonDTO) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border p-4">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-medium">{seatLabel(seat)}s</span>
        <span className="text-xs text-muted-foreground">{people.length}</span>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>

      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No {seatLabel(seat)} on this tab yet
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {people.map((person) => (
            <SeatPersonRow
              key={person.user_id}
              person={person}
              busy={busy}
              onRemove={() => onRemove(person)}
            />
          ))}
        </ul>
      )}

      <AddSeatPicker seat={seat} candidates={candidates} busy={busy} onPick={onPick} />
    </div>
  );
}

// ── Permissions: the tab's functions, grouped by module ─────────────────────

interface ModuleGroup {
  module: string;
  label: string;
  functions: SeatFunctionDTO[];
}

/**
 * The API groups functions by tenant permission module (Invoices, Credit
 * notes, …) and hands them back in that order already, so the grouping is a
 * fold over neighbours rather than a sort — which keeps the server's display
 * order rather than inventing one.
 */
function groupByModule(functions: SeatFunctionDTO[]): ModuleGroup[] {
  const groups: ModuleGroup[] = [];
  for (const entry of functions) {
    const last = groups[groups.length - 1];
    if (last && last.module === entry.module) last.functions.push(entry);
    else groups.push({ module: entry.module, label: entry.module_label, functions: [entry] });
  }
  return groups;
}

function PolicySelect({
  policies,
  value,
  defaultPolicy,
  ariaLabel,
  busy,
  onChange,
}: {
  policies: SeatPolicy[];
  /** Undefined renders as the "Mixed" placeholder — the bulk select's state. */
  value: SeatPolicy | undefined;
  defaultPolicy?: SeatPolicy;
  ariaLabel: string;
  busy: boolean;
  onChange: (policy: SeatPolicy) => void;
}) {
  return (
    <select
      aria-label={ariaLabel}
      value={value ?? ""}
      disabled={busy}
      onChange={(event) => onChange(event.target.value as SeatPolicy)}
      className={cn(INPUT_CLASS, "h-8 w-40 shrink-0 text-[13px]", value && policyTone(value))}
    >
      <option value="" disabled hidden>
        Mixed
      </option>
      {policies.map((policy) => (
        <option key={policy} value={policy}>
          {POLICY_LABEL[policy]}
          {policy === defaultPolicy ? " · default" : ""}
        </option>
      ))}
    </select>
  );
}

function ModuleGroupSection({
  group,
  policies,
  open,
  busy,
  onToggle,
  onSetOne,
  onSetAll,
}: {
  group: ModuleGroup;
  policies: SeatPolicy[];
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onSetOne: (entry: SeatFunctionDTO, policy: SeatPolicy) => void;
  onSetAll: (policy: SeatPolicy) => void;
}) {
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  const first = group.functions[0];
  const uniform = group.functions.every((entry) => entry.policy === first.policy)
    ? first.policy
    : undefined;
  const changed = group.functions.filter((entry) => entry.changed).length;

  return (
    <div className="border-t first:border-t-0">
      <div className="flex items-center gap-3 px-1 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm font-medium"
        >
          <Chevron className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {group.label} ({group.functions.length})
          </span>
          {changed > 0 && (
            <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {changed} changed
            </span>
          )}
        </button>
        <PolicySelect
          policies={policies}
          value={uniform}
          ariaLabel={`Set every ${group.label} function`}
          busy={busy}
          onChange={onSetAll}
        />
      </div>

      {open && (
        <div className="flex flex-col pb-1 pl-6">
          {group.functions.map((entry) => (
            <div key={entry.key} className="flex items-center gap-3 py-1.5 pr-1 hover:bg-muted/20">
              <span className="flex min-w-0 flex-1 items-center gap-2 break-words text-[13px]">
                {entry.changed && (
                  <span
                    aria-label="Changed from the firm default"
                    title="Changed from the firm default"
                    className="size-1.5 shrink-0 rounded-full bg-primary"
                  />
                )}
                {entry.label}
              </span>
              <PolicySelect
                policies={policies}
                value={entry.policy}
                defaultPolicy={entry.default}
                ariaLabel={entry.label}
                busy={busy}
                onChange={(policy) => onSetOne(entry, policy)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What every function on the tab does — hidden entirely until the tab has a
 * Maker, because with nobody making entries there is nothing for a policy to
 * decide.
 */
function PermissionsBlock({
  tab,
  policies,
  openGroups,
  busy,
  onToggleGroup,
  onToggleAll,
  onSetOne,
  onSetAll,
  onReset,
}: {
  tab: SeatTabDTO;
  policies: SeatPolicy[];
  openGroups: Set<string>;
  busy: boolean;
  onToggleGroup: (module: string) => void;
  onToggleAll: (open: boolean) => void;
  onSetOne: (entry: SeatFunctionDTO, policy: SeatPolicy) => void;
  onSetAll: (functions: SeatFunctionDTO[], policy: SeatPolicy) => void;
  onReset: () => void;
}) {
  const groups = groupByModule(tab.functions);
  const anyOpen = groups.some((group) => openGroups.has(group.module));

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-medium">What each function does</span>
        <span className="text-xs text-muted-foreground">
          {tab.functions.length} function{tab.functions.length === 1 ? "" : "s"} ·{" "}
          {tab.counts.review} need review · {tab.counts.blocked} blocked · {tab.changed_count} changed from
          default
        </span>
        <button
          type="button"
          onClick={() => onToggleAll(!anyOpen)}
          className="ml-auto text-xs font-medium text-primary hover:underline"
        >
          {anyOpen ? "Collapse all" : "Expand all"}
        </button>
        {tab.changed_count > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={onReset}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            Reset to firm defaults
          </button>
        )}
      </div>

      <div className="rounded-lg border px-3 py-1">
        {groups.map((group) => (
          <ModuleGroupSection
            key={group.module}
            group={group}
            policies={policies}
            open={openGroups.has(group.module)}
            busy={busy}
            onToggle={() => onToggleGroup(group.module)}
            onSetOne={onSetOne}
            onSetAll={(policy) => onSetAll(group.functions, policy)}
          />
        ))}
      </div>

      <ul className="flex flex-col gap-0.5">
        {policies.map((policy) => (
          <li key={policy} className="text-xs text-muted-foreground">
            <span className={cn("font-medium", policyTone(policy) || "text-foreground")}>
              {POLICY_LABEL[policy]}
            </span>{" "}
            — {POLICY_HINT[policy]}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Summary: the tab in three sentences ────────────────────────────────────

/** "Invoices: Write off, Cancel write-off · Credit notes: Delete". */
function functionsLine(functions: SeatFunctionDTO[]): string {
  return groupByModule(functions)
    .map((group) => `${group.label}: ${group.functions.map((entry) => entry.label).join(", ")}`)
    .join(" · ");
}

function SummaryBlock({
  tab,
  policies,
  warning,
}: {
  tab: SeatTabDTO;
  policies: SeatPolicy[];
  warning: string | null;
}) {
  const whoLine = (policy: SeatPolicy): string => {
    if (policy === "blocked") {
      return tab.checkers.length > 0
        ? `Only ${namesLine(tab.checkers)} can do these.`
        : "Nobody can do these — there is no Checker on this tab.";
    }
    if (policy === "review") {
      if (tab.makers.length === 0) return "Nobody makes entries here, so nothing is queued.";
      return tab.checkers.length > 0
        ? `${namesLine(tab.makers)} enter them, ${namesLine(tab.checkers)} review them.`
        : `${namesLine(tab.makers)} enter them — nobody is set to review them.`;
    }
    return tab.makers.length > 0
      ? `${namesLine(tab.makers)} do these without review.`
      : "Nobody makes entries here yet.";
  };

  // One row per outcome the tab actually produces. A zero row would only ever
  // say "nothing does this", which is the row's own absence.
  const rows = policies
    .map((policy) => ({ policy, functions: tab.functions.filter((entry) => entry.policy === policy) }))
    .filter((row) => row.functions.length > 0);

  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-sm font-medium">What this means on {tab.label}</span>

      <ul className="flex flex-col divide-y divide-border rounded-lg border">
        {rows.map((row) => (
          <li key={row.policy} className="flex flex-col gap-0.5 px-4 py-2.5">
            <div className="flex items-baseline gap-2">
              <span className={cn("text-[13px] font-medium", policyTone(row.policy))}>
                {OUTCOME_HEADING[row.policy]}
              </span>
              <span className="text-xs text-muted-foreground">
                {row.functions.length} function{row.functions.length === 1 ? "" : "s"}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">{whoLine(row.policy)}</span>
            <span className="text-xs text-muted-foreground/80">{functionsLine(row.functions)}</span>
          </li>
        ))}
      </ul>

      {warning && <Callout variant="warning">{warning}</Callout>}
    </div>
  );
}

// ── The reset confirm ──────────────────────────────────────────────────────

function ConfirmDialog({
  open,
  headline,
  body,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  headline: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div
        className="w-full max-w-sm rounded-lg border bg-popover p-5 shadow-md"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-sm font-semibold">{headline}</p>
        <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <CoButton variant="outline" onClick={onCancel}>
            Cancel
          </CoButton>
          <CoButton
            className="border-transparent bg-destructive text-white hover:bg-destructive/90"
            onClick={onConfirm}
          >
            {confirmLabel}
          </CoButton>
        </div>
      </div>
    </div>
  );
}

// ── Step ───────────────────────────────────────────────────────────────────

function TabButton({
  tab,
  active,
  onClick,
}: {
  tab: SeatTabDTO;
  active: boolean;
  onClick: () => void;
}) {
  const seated = tab.makers.length + tab.checkers.length;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active}
      className={cn(
        "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      )}
    >
      {tab.label}
      {/* A permanent "0" beside every tab trains people to stop reading the
          number, so a count only appears once somebody is seated. */}
      {seated > 0 && (
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {seated}
        </span>
      )}
      {tab.gate.status === "block" && (
        <span role="img" aria-label={`${tab.label} is not finished`} className="flex shrink-0">
          <WarningIcon className="size-3.5 text-amber-600 dark:text-amber-400" />
        </span>
      )}
    </button>
  );
}

/**
 * Who works on each module, and what waits for review — six module tabs, and
 * per tab a pool of Makers, a pool of Checkers, and a three-state policy for
 * every function the tab covers.
 *
 * The server owns every rule (the roster requirement, the differences store,
 * the Continue gates) and answers each mutation with the whole screen state,
 * so this component never patches its own copy: it sends the change and renders
 * what comes back. Permissions belong to the TAB, not the person, which is why
 * there is no per-person editor here at all.
 *
 * Two states, deliberately: the seats state is this screen's subject, and the
 * team state is only here for the firm directory — the roster is the identity
 * home, so seating somebody new means team/add first and seats/set second.
 *
 * Switching tabs is never blocked (the strip carries the warning marker
 * instead); Continue is, and by the server's verdict rather than a local one.
 * Every state that arrives is also left behind as the `teamAccess` answer,
 * which is what the workspace preview's "Who has access" section reads on
 * every other screen.
 */
export function TeamAssignmentStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "team-assignment");

  const [state, setState] = useState<SeatsStateDTO | undefined>();
  /** Only for the firm directory the pickers offer. */
  const [team, setTeam] = useState<TeamStateDTO | undefined>();
  /** The load itself failing — different thing from a refused mutation. */
  const [refusal, setRefusal] = useState<string | null>(null);
  /** A refused mutation's sentence — "Add them to the client team first…". */
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  /** `${tabKey}:${module}` for every open group — everything starts collapsed. */
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [resetting, setResetting] = useState<SeatTabDTO | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;

    setRefusal(null);
    Promise.all([fetchSeatsState(clientId), fetchTeamState(clientId)])
      .then(([seats, roster]) => {
        if (!live) return;
        setState(seats);
        setTeam(roster);
        void persistAnswer(clientId, TEAM_PREVIEW_KEY, toTeamPreview(seats));
        void persistAnswer(clientId, APPROVALS_PREVIEW_KEY, seatsToApprovalsPreview(seats));
      })
      .catch((error: unknown) => {
        if (live) setRefusal(error instanceof Error ? error.message : "Could not reach the server.");
      });

    return () => {
      live = false;
    };
  }, [clientId, attempt]);

  if (!client) return null;

  function absorb(next: SeatsStateDTO) {
    setState(next);
    // What the right-hand preview reads on every screen; fire-and-forget the
    // same way ZOHO_CONNECTION_KEY is written. The review-load summary rides
    // along: this screen owns the policies now, so it owns that answer too.
    void persistAnswer(clientId, TEAM_PREVIEW_KEY, toTeamPreview(next));
    void persistAnswer(clientId, APPROVALS_PREVIEW_KEY, seatsToApprovalsPreview(next));
  }

  async function apply(call: () => Promise<SeatsStateDTO>) {
    setBusy(true);
    try {
      absorb(await call());
      setActionError(null);
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : "That change did not go through.");
    } finally {
      setBusy(false);
    }
  }

  const tabs = state?.tabs ?? [];
  const tab = tabs.find((candidate) => candidate.key === activeKey) ?? tabs[0];
  const policies = state?.policies ?? [];
  const blocks = state?.validation.blocks ?? [];
  const ready = state !== undefined && state.validation.ok;

  /**
   * Everybody who could take a seat on this tab: the client's roster minus
   * whoever already sits on it (either seat — one seat per person per tab), then
   * the firm users who are not on the client at all.
   */
  const candidatesFor = (current: SeatTabDTO): SeatCandidate[] => {
    const roster: SeatCandidate[] = (state?.members ?? [])
      .filter((member: SeatMemberDTO) => member.seats[current.key] === undefined)
      .map((member) => ({
        user_id: member.user_id,
        name: member.name,
        email: member.email,
        onRoster: true,
      }));
    const firm: SeatCandidate[] = (team?.candidates ?? []).map((candidate) => ({
      user_id: candidate.user_id,
      name: candidate.name,
      email: candidate.email,
      onRoster: false,
    }));

    return [...roster, ...firm];
  };

  /**
   * Seating somebody the client has never had goes through the roster first —
   * the server refuses a seat for anyone not on onboarding_team_members, and
   * that refusal is the rule, not a race.
   */
  const seat = (current: SeatTabDTO, candidate: SeatCandidate, kind: SeatKind) =>
    void apply(async () => {
      if (!candidate.onRoster) setTeam(await addTeamMember(clientId, candidate.user_id));
      return setSeat(clientId, current.key, candidate.user_id, kind);
    });

  /**
   * A whole module group at once, the mock's bulk dropdown. The API sets one
   * function per call, so this is a sequence — and it skips functions already
   * at the target value, which is what keeps a group of twelve from being
   * twelve round trips when only two of them differ.
   */
  const setGroupPolicy = (functions: SeatFunctionDTO[], policy: SeatPolicy) => {
    const current = state;
    if (!current) return;

    void apply(async () => {
      let latest = current;
      for (const entry of functions) {
        if (entry.policy === policy) continue;
        latest = await setFunctionPolicy(clientId, entry.key, policy);
      }
      return latest;
    });
  };

  const toggleGroup = (tabKey: string, module: string) =>
    setOpenGroups((open) => {
      const next = new Set(open);
      const key = `${tabKey}:${module}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAllGroups = (current: SeatTabDTO, open: boolean) =>
    setOpenGroups((existing) => {
      const next = new Set(existing);
      for (const group of groupByModule(current.functions)) {
        const key = `${current.key}:${group.module}`;
        if (open) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  /** The groups open on THIS tab, with the tab prefix stripped off. */
  const openOn = (current: SeatTabDTO): Set<string> =>
    new Set(
      groupByModule(current.functions)
        .map((group) => group.module)
        .filter((module) => openGroups.has(`${current.key}:${module}`))
    );

  /**
   * The one line under the tab: a block reads as the gate's own sentence, a
   * warning as the validation warning about this tab. Everything else says
   * nothing — an untouched tab is a legitimate answer.
   */
  const tabNotice = (current: SeatTabDTO): string | null => {
    if (current.gate.status === "block") return current.gate.message;
    if (current.gate.status === "warn") {
      return (
        state?.validation.warnings.find((warning) => warning.tab === current.key)?.message ??
        current.gate.message
      );
    }
    return null;
  };

  return (
    <SubStepShell
      title="Assign Your Team"
      description="Add your team members and specify how they will work on each module."
      thenWhat={ready ? "Next: Set Up Client Contacts" : (blocks[0]?.message ?? "Continue opens once every tab is in order.")}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      <div className="flex flex-col gap-1 rounded-lg border bg-muted/20 px-5 py-4 text-sm text-muted-foreground">
        <p>
          For each module below, select the Makers and Checkers responsible for the work. Makers perform the
          day-to-day work, while Checkers review and approve work before it is posted.
        </p>
        <p>A Checker also has all the privileges of a Maker.</p>
        <p>If there is no Checker assigned to a module, the Maker can post directly.</p>
        <p>Makers and Checkers can be configured separately for each module.</p>
      </div>

      {state === undefined && refusal === null ? (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          Reading the module tabs…
        </div>
      ) : refusal !== null ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed px-5 py-4">
          <p className="text-sm text-muted-foreground">{refusal}</p>
          <CoButton variant="outline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </CoButton>
        </div>
      ) : tab === undefined ? (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          No module tabs came back from the server.
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-1 border-b">
            {tabs.map((candidate) => (
              <TabButton
                key={candidate.key}
                tab={candidate}
                active={candidate.key === tab.key}
                onClick={() => setActiveKey(candidate.key)}
              />
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <SeatColumn
              seat="maker"
              hint="Makers do the day-to-day work on this module."
              people={tab.makers}
              candidates={candidatesFor(tab)}
              busy={busy}
              onPick={(candidate) => seat(tab, candidate, "maker")}
              onRemove={(person) => void apply(() => removeSeat(clientId, tab.key, person.user_id))}
            />
            <SeatColumn
              seat="checker"
              hint="Checkers share one review queue — first to approve wins."
              people={tab.checkers}
              candidates={candidatesFor(tab)}
              busy={busy}
              onPick={(candidate) => seat(tab, candidate, "checker")}
              onRemove={(person) => void apply(() => removeSeat(clientId, tab.key, person.user_id))}
            />
          </div>

          {/* No Maker, no permissions: with nobody making entries on this tab
              there is nothing for a policy to decide, and the mock hides the
              whole table for exactly that reason. */}
          {tab.makers.length > 0 && (
            <PermissionsBlock
              tab={tab}
              policies={policies}
              openGroups={openOn(tab)}
              busy={busy}
              onToggleGroup={(module) => toggleGroup(tab.key, module)}
              onToggleAll={(open) => toggleAllGroups(tab, open)}
              onSetOne={(entry, policy) =>
                void apply(() => setFunctionPolicy(clientId, entry.key, policy))
              }
              onSetAll={setGroupPolicy}
              onReset={() => setResetting(tab)}
            />
          )}

          <SummaryBlock tab={tab} policies={policies} warning={tabNotice(tab)} />

          {actionError && <p className="text-sm text-destructive">{actionError}</p>}

          {/* The client-wide gate — nobody seated anywhere — belongs under the
              tabs rather than on any one of them. */}
          {blocks
            .filter((block) => block.scope === "client")
            .map((block) => (
              <p key={block.message} className="text-sm text-destructive">
                {block.message}
              </p>
            ))}
        </div>
      )}

      <ConfirmDialog
        open={resetting !== null}
        headline={`Reset ${resetting?.label ?? "this tab"} to the firm defaults?`}
        body={`Every change made to this tab's ${resetting?.changed_count ?? 0} function${
          resetting?.changed_count === 1 ? "" : "s"
        } goes back to the firm's standard policy. The seats stay as they are.`}
        confirmLabel="Reset"
        onCancel={() => setResetting(null)}
        onConfirm={() => {
          if (resetting) void apply(() => resetTabPolicies(clientId, resetting.key));
          setResetting(null);
        }}
      />
    </SubStepShell>
  );
}
